import { EventFeedEvent } from '@api/api/event-feed';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { formatPriceWithCurrency } from '@helpers/utils/currency/currency';
import { config } from '@terminal/config/app.config';
import { useGetEarningById } from '@terminal/react-query-hooks/earnings/useGetEarningById';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { getCardDate } from '@terminal/utils/card-utils/card-utils';
import { CardPill } from '@terminal/views/insight-feed/components/insight-feed-card/components/card-pill/CardPill';
import { CardType } from '@terminal/views/insight-feed/components/insight-feed-card/InsightFeedCard';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from '../../OverviewEventFeed.module.scss';
import { formatScheduledDate, parseEarningsData } from './utils';

export interface EarningsRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
}

export const EarningsRow = ({ event, isLast }: EarningsRowProps) => {
  const { t } = useTranslation(['whatsNew', 'earnings', 'calendar']);
  const { openDrawer } = useEarningsInsightDrawer();

  const isPreview = event.metadata.earnings_type === 'preview';
  const cardType = isPreview
    ? CardType.EarningsPreview
    : CardType.EarningsRecap;
  const earningsId = event.metadata.earnings_id ?? undefined;

  const { data: earningsData } = useGetEarningById(earningsId);
  const { epsValue, epsDelta, currency, reportingDate, isBeat } =
    parseEarningsData(isPreview, earningsData);

  const handleClick = () => {
    if (config.app.isTeamsApp || !earningsId) {
      return;
    }

    openDrawer({
      entityTag: event.entity,
      earningsId,
      subtab: isPreview ? SubTabsType.Preview : SubTabsType.Recap,
      source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
      insightId: event.source_id,
    });
  };

  return (
    <div
      data-testid="earnings-row"
      className={clsx(styles.feedItemWrapper, {
        [styles.last]: isLast,
        [styles.notClickable]: config.app.isTeamsApp,
      })}
    >
      <div className={styles.feedItem} onClick={handleClick}>
        <div className={styles.feedItemHeader}>
          <CardPill type={cardType} />
        </div>
        <p className={styles.insightTitle}>{event.title}</p>
        <div className={styles.statsRow}>
          <div className={styles.statsContainer}>
            {epsValue !== undefined && (
              <div className={styles.earningsLabelWrapper}>
                <span className={styles.statLabel}>
                  {isPreview
                    ? t('earnings:earningsInsight.drawer.epsExpectations')
                    : t('earnings:earningsInsight.drawer.epsActual')}
                </span>
                <span className={styles.priceValue}>
                  {formatPriceWithCurrency(epsValue, currency)}
                </span>
              </div>
            )}
            {isPreview && reportingDate && (
              <>
                <div className={styles.verticalDivider} />
                <div className={styles.earningsLabelWrapper}>
                  <span className={styles.scheduledLabel}>
                    {t('calendar:scheduledFor')}
                  </span>
                  <span className={styles.scheduledDate}>
                    {formatScheduledDate(new Date(reportingDate))}
                  </span>
                </div>
              </>
            )}
            {!isPreview && epsDelta !== undefined && epsValue !== undefined && (
              <>
                <div className={styles.verticalDivider} />
                <div className={styles.earningsLabelWrapper}>
                  <span
                    className={clsx(styles.directionalValue, {
                      [semanticColorStyles.directionPositive]: isBeat,
                      [semanticColorStyles.directionNegative]: !isBeat,
                    })}
                  >
                    {isBeat ? epsDelta : Math.abs(epsDelta)}%
                  </span>
                  <span
                    className={clsx(styles.directionalText, {
                      [semanticColorStyles.directionPositive]: isBeat,
                      [semanticColorStyles.directionNegative]: !isBeat,
                    })}
                  >
                    {t(
                      isBeat
                        ? 'earnings:earningsInsight.drawer.beat'
                        : 'earnings:earningsInsight.drawer.miss'
                    )}
                  </span>
                </div>
              </>
            )}
          </div>
          <span className={styles.timeAgo}>
            {getCardDate(new Date(event.created_at))}
          </span>
        </div>
      </div>
    </div>
  );
};
